import { Result } from 'antd';
import LoadingSpinner from 'components/LoadingSpinner';
import Map from 'features/Map';
import SideMenu from 'features/SideMenu';
import { setShowStaticMap } from 'features/vicinityMapSlice';
import config from 'lib/config';
import {
  DEFAULT_CATEGORY_COLOR,
  DEFAULT_CATEGORY_TEXT_COLOR,
  DEFAULT_FONT_FAMILY,
  DEFAULT_POI_COLOR,
  DEFAULT_POI_TEXT_COLOR,
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_PRIMARY_TEXT_COLOR,
  DEFAULT_SIDE_MENU_BACKGROUND_COLOR,
  DEFAULT_SIDE_MENU_POSITION,
  DEFAULT_SIDE_MENU_WIDTH,
  MIN_SIDE_MENU_WIDTH,
} from 'lib/constants';
import gaFunction from 'lib/ga';
import GlobalStyles from 'lib/globalStyles';
import { useVicinityMapQuery } from 'lib/hooks/queries';
import { useAppDispatch, useAppSelector } from 'lib/hooks/redux';
import useWindowDimensions from 'lib/hooks/useWindowDimensions';
import { generateStaticMapURL } from 'lib/utils';
import { useEffect } from 'react';
import styled, { DefaultTheme, ThemeProvider } from 'styled-components';

function App() {
  const dispatch = useAppDispatch();
  const windowDimensions = useWindowDimensions();
  // initialize state
  const { isSideMenuCollapsed, showStaticMap } = useAppSelector((state) => ({
    isSideMenuCollapsed: state.vicinityMap.isSideMenuCollapsed,
    showStaticMap: state.vicinityMap.showStaticMap,
  }));

  const { isMobile } = useWindowDimensions();

  // get the URL parameters
  const URLParams = new URLSearchParams(window.location.search);
  const communityId = URLParams.get('communityId');

  // get vicinity map data
  const vicinityMapQuery = useVicinityMapQuery({ communityId });
  const vicinityMapData = vicinityMapQuery.data;

  // GA function
  useEffect(() => {
    if (vicinityMapData && communityId) {
      gaFunction.initBasicTracking('web', 'vm');
      gaFunction.setClientInformation(
        vicinityMapData.client.sysId,
        vicinityMapData.client.name,
        vicinityMapData.client.clientDivision
      );
      gaFunction.setCommunityInformation(communityId, vicinityMapData.community.name);
      gaFunction.startSession();
    }
  }, [communityId, vicinityMapData]);

  // set the showStaticMap state
  useEffect(() => {
    if (vicinityMapData && vicinityMapData.main.staticMapUrl) {
      dispatch(setShowStaticMap(true));
    }
  }, [vicinityMapData, dispatch]);

  // display error on missing URL params
  if (!communityId) {
    return <Result status="error" title="Missing necessary params" />;
  }

  // display error on handled API error
  if (vicinityMapQuery.error) {
    return <Result status="error" title={vicinityMapQuery.error.message} />;
  }

  // errors handled, if no data, means query is loading
  if (!vicinityMapData) {
    return <LoadingSpinner data-cy="loading-LoadingSpinner" />;
  }

  // display error on no project location coordinates
  if (!vicinityMapData.main.projectLocationCoordinates) {
    return (
      <Result
        status="error"
        title="No project location coordinates found."
        subTitle="Please update the project address in the community admin console to continue."
      />
    );
  }

  // display error when the version doesn't match
  if (!config.version.startsWith(vicinityMapData.appInfo.versionNumber)) {
    return (
      <Result
        status="error"
        title="App version mismatch"
        subTitle="Please contact Focus360 for the right version of this app."
      />
    );
  }

  // Global Styles and Theming
  const {
    font,
    sideMenuPosition,
    sideMenuWidth,
    sideMenuBackgroundColor,
    primaryColor,
    primaryTextColor,
    buttonColorCategory,
    buttonColorPoi,
    textColorCategory,
    textColorPoi,
    logoWidth,
  } = vicinityMapData.main;

  const theme: DefaultTheme = {
    font: {
      fontFamily: font || DEFAULT_FONT_FAMILY,
    },
    layout: {
      sideMenuPosition: sideMenuPosition || DEFAULT_SIDE_MENU_POSITION,
      sideMenuWidth: isMobile
        ? `${MIN_SIDE_MENU_WIDTH}px`
        : sideMenuWidth
        ? `max(${sideMenuWidth}%, ${MIN_SIDE_MENU_WIDTH}px)`
        : `max(${DEFAULT_SIDE_MENU_WIDTH}%, ${MIN_SIDE_MENU_WIDTH}px)`,
      isSideMenuCollapsed,
      isMobile,
      logoWidth: logoWidth || 175,
    },
    colors: {
      sideMenuBackgroundColor: sideMenuBackgroundColor || DEFAULT_SIDE_MENU_BACKGROUND_COLOR,
      primaryTextColor: primaryTextColor || DEFAULT_PRIMARY_TEXT_COLOR,
      primaryColor: primaryColor || DEFAULT_PRIMARY_COLOR,
      buttonColorCategory: buttonColorCategory || DEFAULT_CATEGORY_COLOR,
      textColorCategory: textColorCategory || DEFAULT_CATEGORY_TEXT_COLOR,
      buttonColorPoi: buttonColorPoi || DEFAULT_POI_COLOR,
      textColorPoi: textColorPoi || DEFAULT_POI_TEXT_COLOR,
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles
        maxInfoWindowWidth={windowDimensions.width - 70}
        customCSS={vicinityMapData.main.jsonCustomCss || ''}
      />
      <StyledAppContainer>
        {/* if there is a static map url and the static map has not been clicked, show Static Map along with sideMenu*/}
        {showStaticMap ? (
          <>
            <SideMenu />
            <StaticMap
              src={generateStaticMapURL(vicinityMapData)}
              id="static-google-map"
              data-cy="static-google-map"
              alt="static google map"
              onClick={() => {
                dispatch(setShowStaticMap(false));
              }}
              onWheel={() => {
                dispatch(setShowStaticMap(false));
              }}
            />
          </>
        ) : (
          // if there are no static maps OR the user clicked the static map, load the google map, which has a sideMenu component within.
          <Map />
        )}
      </StyledAppContainer>
    </ThemeProvider>
  );
}

export default App;

const StyledAppContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  font-size: 16px;
  font-family: ${({ theme }) => theme.font.fontFamily};
  overflow: hidden;
  position: relative;
`;

const StaticMap = styled.img`
  position: absolute;
  height: 100%;
  width: ${({ theme }) =>
    theme.layout.isMobile ? '100%' : `calc(100% - ${theme.layout.sideMenuWidth})`};
  left: ${({ theme }) => (theme.layout.sideMenuPosition === 'left' ? 'auto' : '0')};
  right: ${({ theme }) => (theme.layout.sideMenuPosition === 'right' ? 'auto' : '0')};
  cursor: pointer;
`;
